import 'swiper/css';
import 'swiper/css/navigation';
import { Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';

import Button from 'components/field/Button';
import IntegerInput from 'components/field/IntegerInput';
import FormHeader from 'components/global/FormHeader';
import NoRecordsFound from 'components/global/NoRecordsFound';
import Skeleton from 'components/global/Skeleton';
import Tabs from 'components/global/Tabs';
import Typography from 'components/global/Typography';
import { HeaderContainerFormHeader } from 'components/global/styled/FormHeader.styled';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useLocale from 'hooks/useLocale';

import { OTO_STORE } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import PATHS from 'utils/paths';

import { faArrowLeft } from '@fortawesome/pro-solid-svg-icons';

import { HeaderCartButton, useTranslateProductName } from './OTOStore';
import * as S from './OTOStoreProductDetails.styled';
import { updateUserCart } from './OTOStoreSlice';

const OTOStoreProductDetails = () => {
	const { showError } = useAlert();
	const axiosPrivate = useAxiosPrivate();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const userCart = useSelector((state) => state.otoStore.cart);
	const { t } = useTranslation();
	const { Currency, LanguageCode } = useLocale();
	const { translateProductName } = useTranslateProductName();

	const [searchParams] = useSearchParams();
	const productId = searchParams.get('productId');
	const productName = searchParams.get('name');

	const [productData, setProductData] = useState(null);
	const [swiperRef, setSwiperRef] = useState(null);
	const [isLoading, setIsLoading] = useState(false);
	const [currentIndex, setCurrentIndex] = useState(0);

	const productCartQuantity = useMemo(
		() => userCart?.items?.find((item) => +item.productId === +productId)?.quantity,
		[userCart, productId]
	);

	const getProductDetails = async (productId) => {
		try {
			setIsLoading(true);
			if (typeof productId !== 'string') {
				throw new Error('productId is null');
			}
			const response = await axiosPrivate.post(OTO_STORE.GET_PRODUCT_DETAILS, { productId });
			if (response.status === 200) {
				setProductData(response.data);
			}
		} catch (error) {
			AppLogger.error(error);
			showError();
		} finally {
			setIsLoading(false);
		}
	};

	const slideTo = (index) => {
		swiperRef.slideTo(index, 0);
	};

	useEffect(() => {
		getProductDetails(productId);
	}, [productId]);

	const handleAddToCart = async () => {
		try {
			await dispatch(
				updateUserCart({
					productId,
					quantity: productData?.quantity || productCartQuantity || 1
				})
			).unwrap();
		} catch (error) {
			showError();
			AppLogger.error(error);
		}
	};

	const handleBuyNow = () => {
		handleAddToCart().then(() => {
			navigate(PATHS.OTO_STORE_CHECKOUT);
		});
	};

	const handleChangeQuantity = (newValue) => {
		setProductData((prev) => ({ ...prev, quantity: newValue }));
	};

	const imageGallery = useMemo(() => {
		if (productData) {
			const images = productData?.productAttributes?.filter?.((item) => item?.attributeName === 'image') || [];

			if (productData.imageURL) {
				images?.splice(0, 0, { attributeValue: productData.imageURL });
			}
			if (images?.length === 0) {
				return [{ value: '' }];
			}
			return images?.map((item) => ({ image: item?.attributeValue }));
		} else {
			return [{ value: '' }];
		}
	}, [productData]);

	const findAttribute = useCallback(
		(key) => productData?.productAttributes?.find?.((item) => item?.attributeName === key)?.attributeValue,
		[productData]
	);

	const translatedProductName = useMemo(() => translateProductName(productData), [productData, LanguageCode]);

	const tabs = useMemo(
		() => [
			{
				label: 'description',
				component: () => <DescriptionTab productData={productData} />
			}
		],
		[isLoading, productData, LanguageCode]
	);

	if (!isLoading && !productData) {
		return (
			<div>
				<HeaderContainerFormHeader>
					<FormHeader boldTitle="otoStore" icon={faArrowLeft} backUrl={PATHS.OTO_STORE} />
					<HeaderCartButton />
				</HeaderContainerFormHeader>
				<S.ProductNotFound>
					<NoRecordsFound title="otoStoreProductNotFound" subTitle="otoStoreProductNotFoundDescription" />
				</S.ProductNotFound>
			</div>
		);
	}

	return (
		<div>
			<HeaderContainerFormHeader>
				<FormHeader
					boldTitle={translatedProductName || productName}
					lightTitle="otoStore"
					icon={faArrowLeft}
					backUrl={PATHS.OTO_STORE}
				/>
				<HeaderCartButton />
			</HeaderContainerFormHeader>
			<S.PageContainer>
				<S.ProductDetailsContainer>
					<S.ImageContainer>
						<S.SwiperArea>
							{isLoading ? (
								<Skeleton height="45rem" width="100%" />
							) : (
								<Swiper
									dir="ltr"
									slidesPerView={1}
									spaceBetween={10}
									navigation={true}
									onSwiper={setSwiperRef}
									onActiveIndexChange={(swiper) => setCurrentIndex(swiper.activeIndex)}
									modules={[Navigation]}>
									{imageGallery?.map((images, index) => (
										<SwiperSlide key={index}>
											<S.ProductImage
												url={images.image}
												alt={productName}
												key={index}
												width="100%"
												height="45rem"
											/>
										</SwiperSlide>
									))}
								</Swiper>
							)}
						</S.SwiperArea>
						<S.ProductThumbCarousel>
							{isLoading
								? Array.from({ length: 4 }).map((_, index) => (
										<Skeleton key={index} height="15rem" width="15rem" />
									))
								: imageGallery.map((images, index) => (
										<S.ProductThumbButton
											onClick={() => slideTo(index)}
											key={index}
											isActive={currentIndex === index}>
											<S.ProductThumb
												url={images.image}
												alt={productName}
												key={index}
												width="15rem"
												height="15rem"
											/>
										</S.ProductThumbButton>
									))}
						</S.ProductThumbCarousel>
					</S.ImageContainer>
					<S.DetailsContainer>
						{isLoading ? (
							<>
								<S.DetailBox gap="1rem" className="mb-2-5">
									<Skeleton height="2.5rem" width="40%" />
									<Skeleton height="2.5rem" width="100%" />
								</S.DetailBox>
								<Skeleton height="4rem" width="8rem" />
							</>
						) : (
							<>
								<S.DetailBox gap="1rem" className="mb-2-5">
									<Typography variant="h4" noWrap>
										{translatedProductName}
									</Typography>
								</S.DetailBox>
								<Typography variant="h3">
									<Trans
										i18nKey="otoStoreProductPrice"
										values={{
											value: productData?.sellingPrice || productData?.price,
											currency: t(productData?.currency || Currency)
										}}
										components={{
											currencyText: <S.CurrencyText />
										}}
									/>
								</Typography>
							</>
						)}
						<S.Line />
						{isLoading ? (
							<S.SizeDetail>
								<Skeleton height="2.5rem" width="8rem" />
								<Skeleton height="3rem" width="12rem" />
							</S.SizeDetail>
						) : (
							findAttribute('size') && (
								<S.SizeDetail>
									<Typography variant="p2" color="var(--black-40)">
										size
									</Typography>
									<S.SizeButton>
										<Typography variant="p3" autoTranslation={false}>
											{findAttribute('size')}
										</Typography>
									</S.SizeButton>
								</S.SizeDetail>
							)
						)}
						<S.ActionContainer>
							<S.ProductQuantityContainer>
								<IntegerInput
									showArrows
									allowFloat={false}
									min={1}
									max={productData?.maxQty}
									value={productData?.quantity || productCartQuantity || 1}
									setValue={(e) => handleChangeQuantity(e < 1 ? 1 : e)}
									disabled={isLoading}
								/>
							</S.ProductQuantityContainer>
							<Button
								variant="blue"
								label="buyNow"
								fullWidth
								size="xx-large"
								onClick={handleBuyNow}
								loading={isLoading}
							/>
							<Button
								variant="outline"
								label="updateCart"
								fullWidth
								size="xx-large"
								onClick={handleAddToCart}
								loading={isLoading || userCart?.isLoading}
							/>
						</S.ActionContainer>
					</S.DetailsContainer>
				</S.ProductDetailsContainer>
				{!isLoading && findAttribute('longDescription') && (
					<S.ProductDetailsBottomContainer>
						<Tabs data={tabs} />
					</S.ProductDetailsBottomContainer>
				)}
			</S.PageContainer>
		</div>
	);
};

const DescriptionTab = ({ productData }) => {
	const { LanguageCode } = useLocale();

	const getDescription = () => {
		let descriptionKey = '';
		switch (LanguageCode) {
			case 'arabicValue':
				descriptionKey = 'longDescriptionAR';
				break;
			case 'engValue':
				descriptionKey = 'longDescription';
				break;
			default:
				descriptionKey = 'longDescription';
		}

		const description = productData?.productAttributes?.find?.(
			(item) => item?.attributeName === descriptionKey
		)?.attributeValue;
		return description;
	};

	return (
		<Typography noWrap>
			<Trans
				i18nKey={getDescription()}
				components={{
					ul: <ul />,
					li: <li />,
					span: <span />
				}}
			/>
		</Typography>
	);
};

export default OTOStoreProductDetails;
